import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { currencyPrecise, entityLabel, num, percent } from '../format';
import { useReportWidget } from '../hooks/useReportWidget';
import { COLORS, cardSx } from '../theme';
import { ROWS_PER_PAGE_OPTIONS, paginateRows } from './Conversions';
import { pageWindow } from './ImpressionsByType';
import WidgetSkeleton from './WidgetSkeleton';

const KEY = 'googleAdsCampaignBreakdown';

// Same 20/50/100 toggle styling Conversions.tsx/ImpressionsByType.tsx/
// PublisherInventoryTable.tsx each keep their own copy of, rather than a
// shared component.
const darkToggleSx = {
  bgcolor: COLORS.pageBg,
  borderRadius: '10px',
  border: `1px solid ${COLORS.border}`,
  p: 0.5,
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    border: 0,
    borderRadius: '8px !important',
    px: 1.5,
    py: 0.25,
    color: COLORS.muted,
    fontSize: 13,
    fontWeight: 600,
    '&.Mui-selected': { bgcolor: COLORS.black, color: '#fff', '&:hover': { bgcolor: '#000' } },
  },
} as const;

/** Rows/Prev-Next footer, same look/feel as every other paginated Reporting V2
 * grid (flank#5149 follow-up: this card rendered up to 1,000 rows unpaginated). */
const PaginationFooter = ({
  total,
  rowsPerPage,
  safePage,
  pageCount,
  shown,
  onPageChange,
  onRowsPerPageChange,
}: {
  total: number;
  rowsPerPage: number;
  safePage: number;
  pageCount: number;
  shown: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}) => {
  if (total <= ROWS_PER_PAGE_OPTIONS[0]) return null;
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, pt: 1 }}
    >
      <Typography variant='caption' sx={{ color: COLORS.muted }}>
        {(safePage * rowsPerPage + 1).toLocaleString()}–{(safePage * rowsPerPage + shown).toLocaleString()} of{' '}
        {total.toLocaleString()} rows
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant='caption' sx={{ color: COLORS.muted }}>
            Rows
          </Typography>
          <ToggleButtonGroup
            size='small'
            exclusive
            value={rowsPerPage}
            onChange={(_e, v) => v && onRowsPerPageChange(v)}
            sx={darkToggleSx}
          >
            {ROWS_PER_PAGE_OPTIONS.map(n => (
              <ToggleButton key={n} value={n}>
                {n}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box className='rv2-noprint' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button
            size='small'
            disabled={safePage === 0}
            onClick={() => onPageChange(Math.max(0, safePage - 1))}
            sx={{ textTransform: 'none', minWidth: 0, color: COLORS.muted }}
          >
            Prev
          </Button>
          {pageWindow(safePage, pageCount).map((n, i) =>
            n === -1 ? (
              <Typography key={`gap-${i}`} variant='caption' sx={{ color: COLORS.muted, px: 0.5 }}>
                …
              </Typography>
            ) : (
              <Button
                key={n}
                size='small'
                onClick={() => onPageChange(n)}
                sx={{
                  textTransform: 'none',
                  minWidth: 32,
                  px: 1,
                  color: n === safePage ? '#fff' : COLORS.muted,
                  bgcolor: n === safePage ? COLORS.black : 'transparent',
                  '&:hover': { bgcolor: n === safePage ? '#000' : COLORS.pageBg },
                }}
              >
                {n + 1}
              </Button>
            ),
          )}
          <Button
            size='small'
            disabled={safePage >= pageCount - 1}
            onClick={() => onPageChange(Math.min(pageCount - 1, safePage + 1))}
            sx={{ textTransform: 'none', minWidth: 0, color: COLORS.muted }}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * The global Platform filter can be set to anything; this card is always
 * Google Ads only, same as the ticket's own scope (flank#4006) — no
 * widget-level DSP override exists in buildRequest, so this filters the
 * fetched rows instead.
 */
export const googleAdsRows = (rows: ReportRow[]): ReportRow[] => rows.filter(row => row.dsp === 'googleads');

/**
 * flank#4006 — campaign-level data (impressions/clicks/conversions) and the
 * cost_per_conversion/conversion_rate ratios built from it are already
 * complete in canonical; Attribution Model, Phone Calls and primary-only Conversions
 * have no canonical column at all yet (confirmed against CANONICAL.PERFORMANCE,
 * not just the landing feed). The ticket's own AC already defines a graceful
 * default for each — "N/A when none is set" / "0, not blank" — so those three
 * render as fixed placeholders rather than fabricating backend data for them.
 */
const COLUMNS: { key: string; label: string; align?: 'right' }[] = [
  { key: 'campaign', label: 'Campaign' },
  { key: 'impressions', label: 'Impressions', align: 'right' },
  { key: 'clicks', label: 'Clicks', align: 'right' },
  { key: 'ctr', label: 'CTR', align: 'right' },
  { key: 'cpc', label: 'CPC', align: 'right' },
  { key: 'attribution', label: 'Attribution', align: 'right' },
  { key: 'cost_per_conversion', label: 'Cost / Conv.', align: 'right' },
  { key: 'conversion_rate', label: 'Conv. Rate', align: 'right' },
  { key: 'phone_calls', label: 'Phone Calls', align: 'right' },
  { key: 'conversions', label: 'Conversions', align: 'right' },
  { key: 'all_conversions', label: 'All Conversions', align: 'right' },
];

const GoogleAdsCampaignBreakdownCard = () => {
  const {
    rows: fetchedRows,
    loading,
    error,
  } = useReportWidget(KEY, {
    dimensions: ['dsp', 'campaign', 'campaign_name'],
    metrics: ['impressions', 'clicks', 'ctr', 'cpc', 'conversions', 'cost_per_conversion', 'conversion_rate'],
    sort: [{ field: 'impressions', dir: 'desc' }],
    limit: 1000,
    // Review, Lucas on #5133: Google Ads is a small platform (23 of 933
    // campaigns made the top 1,000 across all DSPs, best rank #86) — without
    // this, the limit applies to every platform's campaigns first and the
    // card silently renders 23 of 933 with no error. `googleAdsRows` below
    // stays as a belt-and-braces guard, not the scoping mechanism.
    dspsOverride: ['googleads'],
  });
  const rows = googleAdsRows(fetchedRows);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROWS_PER_PAGE_OPTIONS[0]);
  const { pageRows, pageCount, safePage } = paginateRows(rows, page, rowsPerPage);

  return (
    <Box sx={{ ...cardSx, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant='subtitle1' sx={{ fontWeight: 600, color: COLORS.text }}>
        Google Ads — Campaign Breakdown
      </Typography>

      {loading ? (
        <WidgetSkeleton height={280} />
      ) : error ? (
        <Typography variant='body2' color='error'>
          {error}
        </Typography>
      ) : rows.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          No Google Ads campaigns for the selected range yet.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                {COLUMNS.map(col => (
                  <TableCell key={col.key} align={col.align} sx={{ color: COLORS.muted, fontWeight: 600 }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map(row => (
                <TableRow key={String(row.campaign)}>
                  <TableCell>{entityLabel(row.campaign_name, row.campaign, 'campaign')}</TableCell>
                  <TableCell align='right'>{num(row.impressions)}</TableCell>
                  <TableCell align='right'>{num(row.clicks)}</TableCell>
                  <TableCell align='right'>{percent(row.ctr)}</TableCell>
                  <TableCell align='right'>{currencyPrecise(row.cpc)}</TableCell>
                  {/* No bidding_strategy_type/attribution column in canonical yet (flank#4006). */}
                  <TableCell align='right'>N/A</TableCell>
                  <TableCell align='right'>{currencyPrecise(row.cost_per_conversion)}</TableCell>
                  <TableCell align='right'>{percent(row.conversion_rate)}</TableCell>
                  {/* Call tracking isn't ingested yet; 0 matches the ticket's own
                      "no call tracking on this account" default. */}
                  <TableCell align='right'>0</TableCell>
                  {/* The feed's conversions match Google's all_conversions (flank#4010: 0.15% off the
                      keywords feed's all_conversions), so they render as All Conversions; Google's
                      primary-only "Conversions" isn't delivered. */}
                  <TableCell align='right'>N/A</TableCell>
                  <TableCell align='right'>{num(row.conversions)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      <PaginationFooter
        total={rows.length}
        rowsPerPage={rowsPerPage}
        safePage={safePage}
        pageCount={pageCount}
        shown={pageRows.length}
        onPageChange={setPage}
        onRowsPerPageChange={next => {
          setRowsPerPage(next);
          setPage(0);
        }}
      />
    </Box>
  );
};

export default GoogleAdsCampaignBreakdownCard;
